import React, { useEffect, useRef, useState } from 'react'
import queryString from 'query-string'

import Analytics from 'utils/googleAnalytics'

import { useAppSelector } from 'redux/hooks'
import { selectCardMap } from 'redux/deck/selectors'

import { useReplaceResults } from 'hooks/useReplaceCard'

import ToastService from 'services/toast.service'
import CardService from 'services/card.service'
import SaveService from 'services/save.service'

import {
  Tab as TabType,
  generateTabDOMId,
  ArchidektSearchMeta,
  ScryfallSearchMeta,
  ManabaseGeneratorMeta,
} from 'types/searchV2'
import { CardType } from 'types/deck'
import { CardListResponse } from 'services/apiTypes/card.types'

import ScryfallSearchForm from '../searchFormElements/scryfallSearchForm'
import ArchidektSearchForm from '../searchFormElements/archidektSearchForm'
import CardResultsGrid from '../cardLayouts/cardResultsGrid'
import EdhRecFrom from '../searchFormElements/edhRecsForm'
import ManabaseGeneratorForm from 'components/cardSearchPanel/searchFormElements/manabaseGeneratorForm'
import SpellbookComboProvider from 'components/cardSearchPanel/searchFormElements/spellbookComboProvider'
import LineLoader from 'components/elements/LineLoader'
import { ButtonPaginationControls } from 'components/collectionV2/misc/PaginationControls'

import styles from './tab.module.scss'

type Props = {
  tab: TabType
  active: boolean
  onTabUpdate: (updatedTab: TabType) => void

  className?: string
  initailResults?: CardType[]

  enableDragging?: boolean

  scrollToTop?: () => void
}

// Used to display all tab content (only ever displays the active tab at a time @see SearchV2)
const Tab = ({ tab, onTabUpdate, active, initailResults, scrollToTop, enableDragging = false }: Props) => {
  const resultsRef = useRef<CardType[]>()

  const [results, setResults] = useState(initailResults ?? [])

  const [next, setNext] = useState<string | null>(null)
  const [totalPages, setTotalPages] = useState<number | null>(null)
  const [page, setPage] = useState<number | null>(null)

  const [loading, setLoading] = useState(false)

  const replacedResults = useReplaceResults(results)

  const cardMap = useAppSelector(selectCardMap)

  // When we hide the pannel, switch back to the list view
  // Doing this prevents getting into a wonky state if you edit a card in the deck, that's also in search, and open
  useEffect(() => {
    if (active) return
  }, [active])

  useEffect(() => {
    resultsRef.current = results
  }, [results])

  useEffect(() => {
    if (tab.type === 'spellbookCombo') {
      setResults([])
      setNext(null)
    }
  }, [tab.type])

  useEffect(() => {
    if (!initailResults) return

    setResults(initailResults)
  }, [initailResults])

  const handleRecieveResponse = (
    res: CardListResponse,
    meta: ArchidektSearchMeta | ScryfallSearchMeta | ManabaseGeneratorMeta,
  ) => {
    const updatedTab = { ...tab, meta }

    // @ts-ignore - might be able to fix this
    // Caused by this componenent doing work for both archisearch and scryfall search
    onTabUpdate(updatedTab)
    setResults(res.cards)

    setNext(res.next)
    setTotalPages(Math.ceil(res.total / res.cards.length))
    setPage(1)

    setLoading(false)
  }

  const handleRequestFailure = (message?: string) => {
    ToastService.create(message || 'An unexpected error occured while searching', 'Card Search', 'error')

    setLoading(false)
  }

  const handleLoadNext = (pageNumber: number) => {
    setLoading(true)

    const parsedUrl = queryString.parseUrl(next || '')

    parsedUrl.query.page = `${pageNumber}`

    const replacedNextUrl = `${parsedUrl.url}?${queryString.stringify(parsedUrl.query)}`

    return CardService.scryfallSearch('', replacedNextUrl)
      .then(res => {
        setResults(res.cards)
        setPage(pageNumber)

        if (scrollToTop) scrollToTop()
      })
      .catch(handleRequestFailure)
      .finally(() => setLoading(false))
  }

  const handleUpdateCard = (currentCardId: string | null, updatedCard: CardType) => {
    if (!currentCardId) return Promise.resolve()

    // The reason we update the reuslts is so if you edit a card that isn't in the deck
    // eg: Making something foil (to check price), changing an edition (to see what it looks like), etc
    const upToDateResults = resultsRef.current || results
    const updatedIndex = upToDateResults.findIndex(card => card.id === currentCardId)

    let updatedResults = [...upToDateResults]

    // force this to their defaults prior to actually saving anything
    updatedCard.relatedCardIds = []
    updatedCard.totalPrintingQtyInDeck = 0

    updatedResults[updatedIndex] = updatedCard

    // Never update the results with a quantity. Once a card has a quantity, it'll be replaced by the replacement hook, then you'll see the in-deck copy (which has a quantity).
    // This prevents us from having to remove any quantities from the results in the case of a card being removed after being added
    updatedResults = updatedResults.map(c => ({ ...c, qty: 0 }))

    // If we don't have a quantity, and it's not in the cardMap, don't update anything
    if (!updatedCard.qty && !cardMap[currentCardId]) {
      setResults(updatedResults)

      return Promise.resolve()
    }

    // prettier-ignore
    if (updatedCard.qty && !cardMap[currentCardId]) Analytics.signalEvent('Added card from search')

    // We strip the category info for cards in search. We can add that back when we go to add the card to the deck
    // The stripping happens so when a user sets a category, we auto add the card to the deck
    if (updatedCard.qty && updatedCard.globalCategories.length && !updatedCard.categories.length)
      updatedCard.categories = [...updatedCard.globalCategories]

    return SaveService.save(updatedCard, [], true).then(() => {
      setResults(updatedResults)
    })
  }

  if (!active) return null

  return (
    <div className={styles.container}>
      {loading && <LineLoader fixed />}

      {tab.type === 'archidektSearch' && (
        <ArchidektSearchForm
          submitText="Search"
          mainInputId={generateTabDOMId(tab.id)}
          noAutoSearch={replacedResults.length > 0 || tab.noAutoSearch}
          searchOptions={tab.meta}
          onSubmitted={() => setLoading(true)}
          onRequestFailed={() => setLoading(false)}
          onResponseRecieved={handleRecieveResponse}
        />
      )}

      {tab.type === 'scrySearch' && (
        <ScryfallSearchForm
          submitText="Search"
          mainInputId={generateTabDOMId(tab.id)}
          noAutoSearch={replacedResults.length > 0 || tab.noAutoSearch}
          searchString={tab.meta.query}
          smartFilters={tab.meta.smartFilters}
          onSubmitted={() => setLoading(true)}
          onRequestFailed={() => setLoading(false)}
          onResponseRecieved={handleRecieveResponse}
        />
      )}

      {tab.type === 'edhRecs' && (
        <EdhRecFrom
          noAutoSearch={replacedResults.length > 0 || tab.noAutoSearch}
          alreadyHasResults={replacedResults.length > 0}
          onSubmitted={() => setLoading(true)}
          onRequestFailed={handleRequestFailure}
          onResponseRecieved={cards => {
            setResults(cards)
            setLoading(false)
            setNext(null)
          }}
        />
      )}

      {tab.type === 'manabaseGenerator' && (
        <ManabaseGeneratorForm
          noAutoSearch={replacedResults.length > 0 || tab.noAutoSearch}
          alreadyHasResults={replacedResults.length > 0}
          onSubmitted={() => setLoading(true)}
          onRequestFailed={handleRequestFailure}
          onResponseRecieved={handleRecieveResponse}
        />
      )}

      {tab.type === 'spellbookCombo' && (
        <SpellbookComboProvider
          noAutoSearch={replacedResults.length > 0 || tab.noAutoSearch}
          alreadyHasResults={replacedResults.length > 0}
          onSubmitted={() => setLoading(true)}
          onRequestFailed={() => setLoading(false)}
          onResponseRecieved={() => setLoading(false)}
          handleCardUpdated={handleUpdateCard}
        />
      )}

      <CardResultsGrid onCardUpdated={handleUpdateCard} cards={replacedResults} />

      {next && (
        <ButtonPaginationControls
          className={styles.paginator}
          activePage={page || 0}
          totalPages={totalPages || 0}
          onPageChange={handleLoadNext}
        />
      )}
    </div>
  )
}

export default Tab
